
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount,
  ref,
  Ref,
  watch
} from "vue";
import { useStore } from "vuex";
import {
  dispatchErrorBox,
  dispatchSuccessBox,
  formattedDate,
  scrollTo
} from "../../components/mixins";
import PaginationDefault from "../../components/application/shop/PaginationDefault.vue";
import { useRoute } from "vue-router";
import router from "../../../../src/routes";
import { Product } from "../../../../domain/entities/shop/product/product";
import { User } from "../../../../domain/entities/entities";
import { RepositoryException } from "../../../../domain/exceptions/exceptions";
import TUIViewer from "../../components/application/TUIViewer.vue";
import HoverGallery from "../../components/application/shop/HoverGallery.vue";

export default defineComponent({
  components: {
    HoverGallery,
    TUIViewer,
    PaginationDefault
  },

  setup() {
    const store = useStore();
    const route = useRoute();

    /** List of products that are in the favorites list. */
    const favoriteProducts: ComputedRef<Array<Product>> = computed(() => {
      return store.getters["shop_module/productsInFavoriteList"];
    });

    /** List of products in the cart. */
    const bagProducts: ComputedRef<Array<Product>> = computed(() => {
      return store.getters["shop_module/productsInBag"];
    });

    /** Number of pages coming from request. */
    const pages: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/page"];
    });

    /** Variable that gets the current page through the route. */
    const currentPage: Ref<number> = ref(Number(route.query.page));

    /** Variable to get the user data. */
    const user: ComputedRef<User> = computed(() => {
      return store.getters["userProfile_module/user"];
    });

    /** Getter of the decoins with the subtraction of the values of the products added in the bag. */
    const balance: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/balance"];
    });

    /** Updates the current page. */
    watch(
      () => currentPage.value,
      async () => {
        await router.push({
          name: "favorite",
          query: { page: currentPage.value }
        });
        await loadFavoriteProducts(currentPage.value, 6);

        /** scroll to the top of view */
        scrollTo(0, 0);
      }
    );

    /** Checks if the current page exists in pagination. When all products are removed from a page the
     * user is directed to the first page. */
    watch(
      () => pages.value,
      async () => {
        if (currentPage.value > pages.value) {
          currentPage.value = 1;
          await router.push({
            name: "favorite",
            query: { page: currentPage.value }
          });
        }
        await loadFavoriteProducts(currentPage.value, 6);
      }
    );

    onBeforeMount(async () => {
      try {
        await store.dispatch("userProfile_module/loadUser");
        await loadFavoriteProducts(currentPage.value, 6);
        await store.dispatch("shop_module/loadBagProducts");
        await store.dispatch(
          "shop_module/setDecoinsInWallet",
          user.value.decoins
        );
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    });

    /** Loads all the products the user added in the favorites list. */
    async function loadFavoriteProducts(
      page: number,
      limit: number
    ): Promise<void> {
      try {
        await store.dispatch("shop_module/loadFavoriteProducts", {
          page: page,
          limit: limit
        });
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for validating the user's decoins along with the stock.
     * Function used to remove the add to bag button.
     * @param product product to validate stock and decoins. */
    function validateStockAndDecoins(product: Product): boolean {
      if (hasEnoughDecoins(product) && isProductOutOfStock(product)) {
        return true;
      } else if (!hasEnoughDecoins(product) && isProductOutOfStock(product)) {
        return true;
      } else if (hasEnoughDecoins(product) && !isProductOutOfStock(product)) {
        return true;
      }
      return false;
    }

    /** Function to remove products from list favorite. */
    async function removeFromFavoriteList(productID: number): Promise<void> {
      try {
        await store.dispatch(
          "shop_module/removeProductFromFavorites",
          productID
        );
        await loadFavoriteProducts(currentPage.value, 6);
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for adding the product to the bag.
     * @param product parameter that receives an object of type Product as a variable.
     * @see Product */
    async function addToBag(product: Product): Promise<void> {
      try {
        product.quantity = 1;
        if (product.stock <= 0) {
          return;
        } else if (product.decoins > user.value.decoins) {
          return;
        } else if (balance.value < product.decoins) {
          return;
        } else if (product.isActive === false) {
          return;
        } else {
          await store.dispatch("shop_module/addToBag", product);
          await dispatchSuccessBox("Produto adicionado no seu carrinho!");
        }
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function to check if the product in the favorites is already in the cart. */
    function bagIncludes(product: Product): boolean {
      return Boolean(bagProducts.value.find(item => item.id === product.id));
    }

    /** Remove the product from the cart within the favorites list. */
    async function removeFromBag(product: Product): Promise<void> {
      try {
        await store.dispatch("shop_module/removeFromBag", product);
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Checks if the product that was added to favorites is in stock.*/
    function isProductOutOfStock(product: Product): boolean {
      return product.stock < 1;
    }

    /** Function responsible for validating the user's decoins. If the user does not have enough decoins to buy
     * the product, the add button in the bag is blocked. */
    function hasEnoughDecoins(product: Product): boolean {
      return product.decoins > user.value.decoins;
    }

    /** Function used to control whether for each product added to the bag it is still possible for the user to add
     * the other products that are available
     * @param product product to do validation. */
    function checkEnough(product: Product) {
      const remain = balance.value - product.decoins;
      const inBag = bagIncludes(product);

      if (product.stock <= 0) {
        return true;
      }

      if (inBag) {
        return true;
      }

      if (remain >= 0) {
        return true;
      }

      return false;
    }

    /** Function responsible for displaying a label informing the product status. */
    function setButtonText(product: Product): string {
      if (!checkEnough(product)) {
        return "decoins insuficientes";
      } else if (isProductOutOfStock(product)) {
        return "sem estoque";
      } else {
        return "adicionar no carrinho";
      }
    }

    /** Function responsible for formatting the decoins to Brazilian currency. */
    function formatDecoins(decoins: number) {
      return Intl.NumberFormat("pt-br").format(decoins);
    }

    /** Function responsible for checking if the value of the product is greater than the amount of user decoins.
     * @param product product to do validation. */
    function checkProductDecoins(product: Product): boolean {
      return product.decoins > user.value.decoins;
    }

    /** Function responsible for checking if the product was removed from the shop. */
    function checkProductIsActive(product: Product): boolean {
      return product.isActive === false;
    }

    /** Function responsible for redirecting the user to the product details page. */
    function goToProductDetail(product: Product): void {
      router.push({
        name: "product_detail",
        params: { productID: product.id }
      });
    }

    return {
      favoriteProducts,
      pages,
      currentPage,
      balance,
      isProductOutOfStock,
      formattedDate,
      removeFromFavoriteList,
      addToBag,
      bagIncludes,
      removeFromBag,
      hasEnoughDecoins,
      validateStockAndDecoins,
      checkEnough,
      setButtonText,
      formatDecoins,
      checkProductDecoins,
      checkProductIsActive,
      goToProductDetail
    };
  }
});
